// Wiersz tabeli dostępności pokoi z dynamiczną zawartością

import React from "react";
import { PriceViewMode } from "../../types/pricing.types";
import {
  AssignedRoom,
  MinAvailabilityMap,
} from "../../types/accommodation.types";
import {
  OfferDay,
  BatchAvailabilityStrategy,
  RestrictionInfo,
} from "../../../../types";
import InlineRoomForm from "./InlineRoomForm";
import AveragedPriceBadge from "./AveragedPriceBadge";
import {
  calculateNightPriceWithMarkup,
  MarkupSettings,
} from "../../../../utils/markupCalculations";


interface AvailabilityTableRowProps {
  categoryOptionId: string;
  room: MinAvailabilityMap[string];
  viewMode: PriceViewMode;
  assignedRooms: AssignedRoom[];
  accommodationDays: OfferDay[];
  markup: MarkupSettings | null;
  isExpanded: boolean;
  onToggleExpanded: (categoryOptionId: string) => void;
  strategy?: BatchAvailabilityStrategy;
  restrictionInfo?: RestrictionInfo;
}

const AvailabilityTableRow: React.FC<AvailabilityTableRowProps> = ({
  categoryOptionId,
  room,
  viewMode,
  assignedRooms,
  accommodationDays,
  markup,
  isExpanded,
  onToggleExpanded,
  strategy,
  restrictionInfo,
}) => {
  const isAveraged = strategy?.type === "full-term" || strategy?.type === "extended-term";
  const averagedBadge = isAveraged ? (
    <AveragedPriceBadge
      roomName={room.name}
      strategy={strategy}
      restrictionInfo={restrictionInfo}
    />
  ) : null;

  const maxPersonsPerRoom =
    room.singleBeds + room.doubleBeds * 2 + room.convertibleBeds * 2;

  // Cena końcowa danej nocy: narzut + dopłata weekendowa (wg daty nocy)
  const getPriceWithMarkup = (basePrice: number, nightDate: string): number =>
    calculateNightPriceWithMarkup(basePrice, nightDate, markup);

  // Cena bazowa jest przekreślona, gdy po zaokrągleniu różni się od ceny końcowej
  const isBasePriceStruck = (basePrice: number, finalPrice: number): boolean =>
    !!markup && Math.round(basePrice) !== finalPrice;

  // Funkcja stylowania dla dostępności (z obsługą overbookingu)
  const getAvailabilityStyle = (count: number): string => {
    if (count < 0) {
      return "text-red-700 font-bold bg-red-50 px-2 py-1 rounded";
    }
    if (count === 0) {
      return "text-gray-500 font-medium";
    }
    return "text-green-600 font-medium";
  };

  // Renderuj komórki w zależności od viewMode
  const renderDayCells = () => {
    if (viewMode === "availability") {
      return accommodationDays.map((day, index) => {
        const dateString = day.date.split("T")[0];
        const dayAvailableCount =
          room.dailyAvailability?.[dateString] || room.minCount; // Fallback na minCount jeśli brak danych per dzień

        // Oblicz wykorzystane pokoje na ten konkretny dzień
        const usedCountOnThisDay = assignedRooms
          .filter(
            (assignedRoom) =>
              assignedRoom.categoryOptionId === categoryOptionId &&
              assignedRoom.offerDayId === day.id
          )
          .reduce((sum, room) => sum + room.quantity, 0);

        // Dostępność = dostępne na dzień - wykorzystane na dzień
        const dayAvailability = dayAvailableCount - usedCountOnThisDay;

        return (
          <td key={day.id} className="text-center py-2 px-2 border-b">
            <div className="flex items-center justify-center space-x-1">
              {dayAvailability < 0 && (
                <span
                  className="text-red-500"
                  title={`Overbooking: ${Math.abs(dayAvailability)} pokoi ponad dostępność`}
                >
                  ⚠️
                </span>
              )}
              <span
                className={`text-sm ${getAvailabilityStyle(dayAvailability)}`}
              >
                {dayAvailability}
              </span>
            </div>
          </td>
        );
      });
    }

    if (viewMode === "base_prices" || viewMode === "final_prices") {
      return accommodationDays.map((day) => {
        const dateString = day.date.split("T")[0];
        const pricing = room.pricing[dateString];

        if (!pricing) {
          return (
            <td key={day.id} className="text-center py-2 px-2 border-b">
              <div className="text-xs text-gray-400">Brak cen</div>
            </td>
          );
        }

        const isBasePrices = viewMode === "base_prices";

        return (
          <td key={day.id} className="text-center py-2 px-2 border-b">
            <div className="space-y-1">
              {pricing.single && (
                <div className="text-xs">
                  <span className="text-gray-600">S: </span>
                  {!isBasePrices &&
                    isBasePriceStruck(
                      pricing.single.net,
                      getPriceWithMarkup(pricing.single.net, dateString)
                    ) && (
                      <span className="line-through text-gray-400 mr-1">
                        {pricing.single.net}
                      </span>
                    )}
                  <span className="font-medium">
                    {isBasePrices
                      ? pricing.single.net
                      : getPriceWithMarkup(pricing.single.net, dateString)}{" "}
                    PLN
                  </span>
                  {averagedBadge}
                </div>
              )}
              {pricing.double && (
                <div className="text-xs">
                  <span className="text-gray-600">D: </span>
                  {!isBasePrices &&
                    isBasePriceStruck(
                      pricing.double.net,
                      getPriceWithMarkup(pricing.double.net, dateString)
                    ) && (
                      <span className="line-through text-gray-400 mr-1">
                        {pricing.double.net}
                      </span>
                    )}
                  <span className="font-medium">
                    {isBasePrices
                      ? pricing.double.net
                      : getPriceWithMarkup(pricing.double.net, dateString)}{" "}
                    PLN
                  </span>
                  {averagedBadge}
                </div>
              )}
              {pricing.triple && (
                <div className="text-xs">
                  <span className="text-gray-600">T: </span>
                  {!isBasePrices &&
                    isBasePriceStruck(
                      pricing.triple.net,
                      getPriceWithMarkup(pricing.triple.net, dateString)
                    ) && (
                      <span className="line-through text-gray-400 mr-1">
                        {pricing.triple.net}
                      </span>
                    )}
                  <span className="font-medium">
                    {isBasePrices
                      ? pricing.triple.net
                      : getPriceWithMarkup(pricing.triple.net, dateString)}{" "}
                    PLN
                  </span>
                  {averagedBadge}
                </div>
              )}
            </div>
          </td>
        );
      });
    }

    // Fallback - nie powinno się nigdy zdarzyć
    return [];
  };

  return (
    <>
      <tr
        className={`hover:bg-gray-50 transition-colors ${
          viewMode === "availability" ? "cursor-pointer" : ""
        }`}
        onClick={() => {
          if (viewMode === "availability") {
            onToggleExpanded(categoryOptionId);
          }
        }}
      >
        {/* Kolumna nazwy pokoju */}
        <td className="py-3 px-4 border-b text-left">
          <div>
            <div className="font-medium text-gray-900 text-sm mb-1">
              🏨 {room.name}
            </div>
            <div className="text-xs text-gray-600">
              🛏️ {room.singleBeds > 0 && `${room.singleBeds}×SGL `}
              {room.doubleBeds > 0 && `${room.doubleBeds}×DBL `}
              {room.convertibleBeds > 0 && `${room.convertibleBeds}×TWN `}
            </div>
          </div>
        </td>

        {/* Kolumny dni */}
        {renderDayCells()}

        {/* Kolumna MIN dostępności (tylko dla trybu availability) */}
        {viewMode === "availability" && (
          <td className="text-center py-2 px-2 border-b">
            <div className="flex items-center justify-center space-x-1">
              {(() => {
                let minAvailabilityAcrossDays = Infinity;

                accommodationDays.forEach((day) => {
                  const dateString = day.date.split("T")[0];
                  const dayAvailableCount =
                    room.dailyAvailability?.[dateString] || room.minCount;
                  const usedCountOnThisDay = assignedRooms
                    .filter(
                      (assignedRoom) =>
                        assignedRoom.categoryOptionId === categoryOptionId &&
                        assignedRoom.offerDayId === day.id
                    )
                    .reduce((sum, room) => sum + room.quantity, 0);
                  const dayAvailability =
                    dayAvailableCount - usedCountOnThisDay;
                  minAvailabilityAcrossDays = Math.min(
                    minAvailabilityAcrossDays,
                    dayAvailability
                  );
                });

                // Jeśli nie ma dni, użyj fallback na room.minCount
                const finalMinAvailability =
                  minAvailabilityAcrossDays === Infinity
                    ? room.minCount
                    : minAvailabilityAcrossDays;

                return (
                  <>
                    {finalMinAvailability < 0 && (
                      <span
                        className="text-red-500"
                        title={`Overbooking: ${Math.abs(finalMinAvailability)} pokoi ponad dostępność (najgorszy dzień)`}
                      >
                        ⚠️
                      </span>
                    )}
                    <span
                      className={`text-sm ${getAvailabilityStyle(finalMinAvailability)}`}
                    >
                      {finalMinAvailability}
                    </span>
                  </>
                );
              })()}
            </div>
          </td>
        )}

        {/* Kolumna maksymalnej liczby osób (tylko dla trybu availability) */}
        {viewMode === "availability" && (
          <td className="text-center py-2 px-3 border-b">
            <span className="text-sm text-gray-600">
              {maxPersonsPerRoom} os.
            </span>
          </td>
        )}
      </tr>

      {/* Rozwijany formularz (dla trybu availability) */}
      {viewMode === "availability" && isExpanded && (
        <tr>
          <td colSpan={accommodationDays.length + 3} className="p-0">
            {(() => {
              // Oblicz minimalną dostępność dla ostrzeżenia o overbookingu
              let worstDayAvailability = Infinity;

              accommodationDays.forEach((day) => {
                const dateString = day.date.split("T")[0];
                const dayAvailableCount =
                  room.dailyAvailability?.[dateString] || room.minCount;
                const usedCountOnThisDay = assignedRooms
                  .filter(
                    (assignedRoom) =>
                      assignedRoom.categoryOptionId === categoryOptionId &&
                      assignedRoom.offerDayId === day.id
                  )
                  .reduce((sum, room) => sum + room.quantity, 0);
                const dayAvailability = dayAvailableCount - usedCountOnThisDay;
                worstDayAvailability = Math.min(
                  worstDayAvailability,
                  dayAvailability
                );
              });

              const effectiveAvailability =
                worstDayAvailability === Infinity
                  ? room.minCount
                  : worstDayAvailability;

              return (
                <>
                  {effectiveAvailability < 0 && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-2">
                      <div className="flex items-center">
                        <div className="text-yellow-700">
                          <span className="font-medium">
                            ⚠️ Ostrzeżenie o overbookingu:
                          </span>
                          <p className="text-sm mt-1">
                            Przypisano więcej pokoi niż dostępnych. W najgorszym
                            dniu: przekroczenie o{" "}
                            {Math.abs(effectiveAvailability)} pokoi.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <InlineRoomForm
                    categoryOptionId={categoryOptionId}
                    room={room}
                    availableCount={effectiveAvailability}
                    maxPersonsPerRoom={maxPersonsPerRoom}
                    accommodationDays={accommodationDays}
                    markup={markup}
                    onClose={() => onToggleExpanded(categoryOptionId)}
                  />
                </>
              );
            })()}
          </td>
        </tr>
      )}
    </>
  );
};

export default AvailabilityTableRow;
